import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { D, JAKARTA } from "../ui";
import { MallStudio } from "../mall/MallStudio";
import { AssetCard, AssetRow } from "./AssetCards";
import { AssetDetail } from "./AssetDetail";
import { AssetNewModal } from "./AssetNewModal";
import { LayoutGrid, List, Search, Plus } from "lucide-react";

// ===== Studio Aset — studio profesional: tab kategori, pencarian, grid/list, draf & varian =====
// Alur generate: AI menulis kode pelukis → tersimpan sebagai DRAF → admin memilih:
// Terapkan (jadi versi aktif) / Simpan sebagai Varian / Buang.
export function StudioPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("bangunan"); // bangunan | alam | karakter | kendaraan | mall (Studio AI Mall)
  const [view, setView] = useState("grid");   // grid | list
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | painted | unpainted | draft
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.catalog().then(d => setRows(d.buildings || [])).catch(e => setMsg(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const tabRows = cat === "alam" ? rows.filter(b => b.category === "Alam")
    : cat === "karakter" ? rows.filter(b => b.category === "Karakter")
    : cat === "kendaraan" ? rows.filter(b => b.category === "Kendaraan")
    : rows.filter(b => !["Alam", "Karakter", "Kendaraan"].includes(b.category));
  const filtered = tabRows.filter(b => {
    if (query.trim() && !b.name.toLowerCase().includes(query.trim().toLowerCase())) return false;
    if (filter === "painted" && !b.paintCode) return false;
    if (filter === "unpainted" && b.paintCode) return false;
    if (filter === "draft" && !b.has_draft) return false;
    return true;
  });

  return (
    <div style={{ padding: "22px 20px 30px", fontFamily: JAKARTA }}>
      {/* Tab kategori + toggle tampilan */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 24 }}>
          {[["bangunan", "Bangunan"], ["alam", "Alam"], ["karakter", "Karakter"], ["kendaraan", "Kendaraan"], ["mall", "Mall"]].map(([k, label]) => (
            <button key={k} data-testid={`studio-tab-${k}`} onClick={() => setCat(k)}
              style={{ background: "none", border: 0, padding: "4px 2px 10px", cursor: "pointer", fontSize: 14, fontWeight: cat === k ? 800 : 600,
                color: cat === k ? D.ink : "#a8a496", borderBottom: cat === k ? `2.5px solid ${D.ink}` : "2.5px solid transparent", fontFamily: JAKARTA, transition: "color 0.15s" }}>
              {label}
            </button>
          ))}
        </div>
        {cat !== "mall" && <div style={{ display: "inline-flex", background: "#fff", borderRadius: 12, padding: 4, boxShadow: "0 4px 14px rgba(20,20,20,0.06)" }}>
          {[[LayoutGrid, "grid"], [List, "list"]].map(([Icon, v]) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              style={{ width: 34, height: 30, display: "grid", placeItems: "center", border: 0, borderRadius: 9, cursor: "pointer", background: view === v ? D.ink : "transparent", color: view === v ? "#fff" : "#8a8776", transition: "background-color 0.15s" }}>
              <Icon size={15} />
            </button>
          ))}
        </div>}
      </div>

      {cat === "mall" ? <MallStudio token={token} /> : <>
          {/* Pencarian + filter status */}
          <div style={{ display: "flex", gap: 10, margin: "18px 0" }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#a8a496" }} />
              <input data-testid="studio-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cari aset..."
                style={{ width: "100%", padding: "12px 14px 12px 38px", background: "#fff", border: "1.5px solid #ece9dc", borderRadius: 99, fontSize: 13, outline: "none", fontFamily: JAKARTA }} />
            </div>
            <select data-testid="studio-filter" value={filter} onChange={e => setFilter(e.target.value)}
              style={{ padding: "0 16px", background: "#fff", border: "1.5px solid #ece9dc", borderRadius: 99, fontSize: 13, fontWeight: 600, color: D.ink, fontFamily: JAKARTA, outline: "none", cursor: "pointer" }}>
              <option value="all">Semua status</option>
              <option value="painted">Terlukis</option>
              <option value="unpainted">Perlu AI</option>
              <option value="draft">Ada Draf</option>
            </select>
            <button data-testid="asset-add-btn" onClick={() => setShowNew(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 18px", background: D.blue, color: "#fff", border: 0, borderRadius: 99, fontSize: 13, fontWeight: 700, fontFamily: JAKARTA, cursor: "pointer", transition: "opacity 0.15s", whiteSpace: "nowrap" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
              <Plus size={15} /> Tambah Aset
            </button>
          </div>
          {msg && <p data-testid="studio-list-msg" style={{ fontSize: 12.5, color: "#b45309", fontWeight: 600, margin: "0 2px 12px" }}>{msg}</p>}

          {/* Grid / list aset */}
          {view === "grid" ? (
            <div data-testid="studio-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
              {filtered.map((b, i) => <AssetCard key={b.bid} b={b} idx={i} onOpen={() => setSel(b)} />)}
            </div>
          ) : (
            <div data-testid="studio-list" style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {filtered.map((b, i) => <AssetRow key={b.bid} b={b} idx={i} onOpen={() => setSel(b)} />)}
            </div>
          )}
          {filtered.length === 0 && <p style={{ color: D.sub, fontSize: 13, padding: "10px 2px" }}>Tidak ada aset yang cocok dengan pencarian/filter.</p>}
      </>}

      {sel && <AssetDetail token={token} b={sel} onClose={() => { setSel(null); load(); }} onChanged={(nb) => { setSel(nb); load(); }} />}
      {showNew && <AssetNewModal token={token} rows={rows} defaultCategory={cat === "karakter" ? "Karakter" : cat === "kendaraan" ? "Kendaraan" : cat === "alam" ? "Alam" : "Publik"} onClose={() => setShowNew(false)}
        onCreated={() => { setShowNew(false); load(); setMsg("Aset baru dibuat — klik kartunya lalu Generate Ulang Penuh untuk melukisnya dengan AI"); }} />}
    </div>
  );
}
